import React, { useEffect, useRef, useState } from 'react';
import api from './api';
import { beginGoogleSignIn } from './firebase';

export default function AuthPage({ onAuthSuccess, onContinueAsGuest }) {
  const [tab, setTab] = useState('login');
  const [form, setForm] = useState({
    name: '',
    username: '',
    identifier: '',
    email: '',
    password: '',
    forgotEmail: '',
    newPassword: '',
    confirmPassword: '',
    otp: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const emailCheckTimerRef = useRef(null);

  useEffect(() => {
    try {
      const lastError = sessionStorage.getItem('bg_last_auth_error');
      if (lastError) {
        setError(String(lastError));
        sessionStorage.removeItem('bg_last_auth_error');
      }
    } catch (_err) {
      // ignore
    }
  }, []);

  function updateField(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function resetMessages() {
    setError('');
    setSuccess('');
  }

  function handleTabChange(nextTab) {
    setTab(nextTab);
    setLoading(false);
    setEmailCheckLoading(false);
    setEmailExists(null);
    resetMessages();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    resetMessages();

    try {
      const payload = tab === 'signup'
        ? { name: form.name, username: form.username, email: form.email, password: form.password }
        : { identifier: form.identifier, password: form.password };

      const url = tab === 'signup' ? '/auth/signup' : '/auth/login';
      const res = await api.post(url, payload);
      onAuthSuccess(res.data.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (tab !== 'forgot') return undefined;

    const email = form.forgotEmail.trim().toLowerCase();
    if (emailCheckTimerRef.current) {
      clearTimeout(emailCheckTimerRef.current);
    }

    if (!email) {
      setEmailExists(null);
      setEmailCheckLoading(false);
      return undefined;
    }

    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!looksLikeEmail) {
      setEmailExists(false);
      setEmailCheckLoading(false);
      return undefined;
    }

    setEmailCheckLoading(true);
    setEmailExists(null);

    emailCheckTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.post('/auth/forgot-password/check-email', { email });
        setEmailExists(Boolean(res.data?.data?.exists));
      } catch (_err) {
        setEmailExists(false);
      } finally {
        setEmailCheckLoading(false);
      }
    }, 700);

    return () => {
      if (emailCheckTimerRef.current) {
        clearTimeout(emailCheckTimerRef.current);
      }
    };
  }, [form.forgotEmail, tab]);

  function formatGoogleAuthError(err) {
    const backendMessage = err?.response?.data?.message;
    if (backendMessage) return backendMessage;

    const firebaseCode = err?.code ? String(err.code) : '';
    const firebaseMessage = err?.message ? String(err.message) : '';
    if (firebaseCode && firebaseMessage) return `${firebaseCode}: ${firebaseMessage}`;
    if (firebaseCode) return firebaseCode;
    if (firebaseMessage) return firebaseMessage;
    return 'Google sign-in failed';
  }

  async function handleGoogleAuth() {
    setLoading(true);
    resetMessages();

    try {
      let idToken = null;

      try {
        idToken = await beginGoogleSignIn({ mode: 'popup' });
      } catch (popupErr) {
        const code = popupErr?.code || '';
        if (
          code === 'auth/popup-blocked'
          || code === 'auth/popup-closed-by-user'
          || code === 'auth/operation-not-supported-in-this-environment'
        ) {
          await beginGoogleSignIn({ mode: 'redirect' });
          return;
        }
        throw popupErr;
      }

      const res = await api.post('/auth/firebase/google', { idToken });
      onAuthSuccess(res.data.data);
    } catch (err) {
      setError(formatGoogleAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleSendOtp() {
    setLoading(true);
    resetMessages();
    try {
      const res = await api.post('/auth/forgot-password/send-otp', {
        email: form.forgotEmail.trim().toLowerCase(),
      });
      setSuccess(res.data?.message || 'OTP sent successfully');
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to send OTP');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPasswordSubmit(e) {
    e.preventDefault();
    resetMessages();

    if (form.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError('Confirm password must match new password');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password/reset', {
        email: form.forgotEmail.trim().toLowerCase(),
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
        otp: form.otp.trim(),
      });

      setSuccess(res.data?.message || 'Password updated successfully');
      setForm((prev) => ({
        ...prev,
        forgotEmail: '',
        newPassword: '',
        confirmPassword: '',
        otp: '',
      }));
      setEmailExists(null);
      setTab('login');
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to reset password');
    } finally {
      setLoading(false);
    }
  }

  const forgotEmailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.forgotEmail.trim());
  const canSendOtp = tab === 'forgot' && emailExists === true && forgotEmailLooksValid && !loading;

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes authFloat {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
        @keyframes authGlow {
          0% { opacity: 0.3; transform: scale(0.96); }
          50% { opacity: 0.62; transform: scale(1.04); }
          100% { opacity: 0.3; transform: scale(0.96); }
        }
        @keyframes authCardIn {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes promoSlide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div style={styles.promoWrap}>
        <div style={styles.promoTrack}>
          <span style={styles.promoText}>
            Win direct up to 10,000 coins • Easy withdrawal • Instant matchmaking • Daily rewards • Play now
          </span>
          <span style={styles.promoText}>
            Win direct up to 10,000 coins • Easy withdrawal • Instant matchmaking • Daily rewards • Play now
          </span>
        </div>
      </div>

      <div style={styles.glowOrb} />

      <div style={styles.card}>
        <h2 style={styles.title}>Tavla Pro</h2>
        <br></br>
        {/* <div style={styles.subtitle}>
          {tab === 'login'
            ? 'Login to Continue'
            : tab === 'signup'
              ? 'Register to Start Playing'
              : 'Reset Your Password'}
        </div> */}

        <form onSubmit={tab === 'forgot' ? handleForgotPasswordSubmit : handleSubmit}>
          {tab === 'signup' && (
            <input
              name="name"
              placeholder="Name"
              value={form.name}
              onChange={updateField}
              required
              style={styles.input}
            />
          )}
          {tab === 'signup' ? (
            <>
              <input
                name="username"
                placeholder="Username"
                value={form.username}
                onChange={updateField}
                required
                style={styles.input}
              />
              <input
                name="email"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={updateField}
                required
                style={styles.input}
              />
            </>
          ) : tab === 'forgot' ? (
            <>
              <input
                name="forgotEmail"
                type="email"
                placeholder="Registered Email"
                value={form.forgotEmail}
                onChange={updateField}
                required
                style={styles.input}
              />
              {form.forgotEmail ? (
                <div style={emailCheckLoading ? styles.info : emailExists ? styles.successText : styles.error}>
                  {emailCheckLoading
                    ? 'Checking email...'
                    : emailExists
                      ? 'Registered email found'
                      : 'No account found with this email'}
                </div>
              ) : null}
              <input
                name="newPassword"
                type="password"
                placeholder="New Password"
                value={form.newPassword}
                onChange={updateField}
                minLength={6}
                required
                style={styles.input}
              />
              <input
                name="confirmPassword"
                type="password"
                placeholder="Confirm New Password"
                value={form.confirmPassword}
                onChange={updateField}
                minLength={6}
                required
                style={styles.input}
              />
              <div style={styles.otpRow}>
                <input
                  name="otp"
                  placeholder="Enter OTP"
                  value={form.otp}
                  onChange={updateField}
                  required
                  style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                />
                <button
                  type="button"
                  disabled={!canSendOtp}
                  onClick={handleSendOtp}
                  style={{
                    ...styles.secondaryBtn,
                    opacity: canSendOtp ? 1 : 0.6,
                    cursor: canSendOtp ? 'pointer' : 'not-allowed',
                  }}
                >
                  Send OTP
                </button>
              </div>
            </>
          ) : (
            <input
              name="identifier"
              placeholder="Username or Email"
              value={form.identifier}
              onChange={updateField}
              required
              style={styles.input}
            />
          )}
          {tab !== 'forgot' && (
            <input
              name="password"
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={updateField}
              required
              style={styles.input}
            />
          )}
          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.successText}>{success}</div>}

          <button type="submit" disabled={loading} style={styles.submitBtn}>
            {loading
              ? 'Please wait...'
              : tab === 'signup'
                ? 'Register'
                : tab === 'forgot'
                  ? 'Verify OTP & Update Password'
                  : 'Login'}
          </button>

          {tab !== 'forgot' && (
            <button type="button" disabled={loading} onClick={handleGoogleAuth} style={styles.googleBtn}>
              <span aria-hidden="true" style={styles.googleIcon} title="Google">
                <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.22 3.62l6.9-6.9C36.02 2.44 30.44 0 24 0 14.62 0 6.51 5.38 2.56 13.22l8.06 6.26C12.5 13.06 17.78 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.14-3.08-.4-4.55H24v9.02h12.94c-.56 2.98-2.24 5.5-4.78 7.2l7.73 6.02c4.52-4.18 7.09-10.33 7.09-17.69z"/>
                  <path fill="#FBBC05" d="M10.62 28.48c-.5-1.48-.78-3.06-.78-4.68s.28-3.2.78-4.68l-8.06-6.26C.92 16.46 0 20.12 0 23.8c0 3.68.92 7.34 2.56 10.64l8.06-5.96z"/>
                  <path fill="#34A853" d="M24 48c6.44 0 11.82-2.12 15.76-5.76l-7.73-6.02c-2.14 1.44-4.88 2.3-8.03 2.3-6.22 0-11.5-3.56-13.38-8.98l-8.06 5.96C6.51 42.62 14.62 48 24 48z"/>
                  <path fill="none" d="M0 0h48v48H0z"/>
                </svg>
              </span>
              Continue with Google
            </button>
          )}

          {tab === 'login' && typeof onContinueAsGuest === 'function' && (
            <button type="button" onClick={onContinueAsGuest} style={styles.guestBtn}>
              Continue as Guest
            </button>
          )}

          {tab === 'login' ? (
            <>
              <div style={styles.switchLine}>
                <button type="button" style={styles.linkBtn} onClick={() => handleTabChange('forgot')}>
                  Forgot Password?
                </button>
              </div>
              <div style={styles.switchLine}>
                New User?{' '}
                <button type="button" style={styles.linkBtn} onClick={() => handleTabChange('signup')}>
                  Register here
                </button>
              </div>
            </>
          ) : tab === 'signup' ? (
            <div style={styles.switchLine}>
              Already a user?{' '}
              <button type="button" style={styles.linkBtn} onClick={() => handleTabChange('login')}>
                Login here
              </button>
            </div>
          ) : (
            <div style={styles.switchLine}>
              Back to{' '}
              <button type="button" style={styles.linkBtn} onClick={() => handleTabChange('login')}>
                Login here
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: "linear-gradient(rgba(26,10,0,0.28), rgba(26,10,0,0.28)), url('/img/two_players_back.png')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  promoWrap: {
    position: 'fixed',
    top: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(920px, 96vw)',
    overflow: 'hidden',
    border: '1px solid rgba(212,165,88,0.65)',
    borderRadius: 999,
    background: 'rgba(48, 22, 12, 0.86)',
    zIndex: 5,
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  },
  promoTrack: {
    display: 'flex',
    width: 'max-content',
    animation: 'promoSlide 18s linear infinite',
  },
  promoText: {
    color: '#f6e7c1',
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    padding: '9px 28px',
  },
  glowOrb: {
    position: 'absolute',
    width: 460,
    height: 460,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(212,165,88,0.42) 0%, rgba(212,165,88,0.0) 65%)',
    filter: 'blur(10px)',
    animation: 'authGlow 3.5s ease-in-out infinite',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    background: 'linear-gradient(165deg, rgba(30,13,7,0.95), rgba(54,25,13,0.95))',
    border: '1px solid rgba(212,165,88,0.45)',
    borderRadius: 16,
    padding: 26,
    color: '#f6e7c1',
    boxShadow: '0 20px 45px rgba(0,0,0,0.45)',
    animation: 'authCardIn 420ms ease, authFloat 4.5s ease-in-out infinite',
    backdropFilter: 'blur(3px)',
  },
  title: {
    marginTop: 0,
    marginBottom: 6,
    textAlign: 'center',
    fontSize: 44,
    letterSpacing: 0.4,
    fontWeight: 800,
  },
  subtitle: {
    textAlign: 'center',
    color: '#d8c39a',
    marginBottom: 16,
    fontSize: 14,
  },
  input: {
    width: '100%',
    marginBottom: 10,
    padding: 12,
    borderRadius: 10,
    border: '1px solid rgba(212,165,88,0.48)',
    background: '#2a170f',
    color: '#f6e7c1',
    outline: 'none',
    fontSize: 16,
  },
  error: {
    color: '#ff9c9c',
    marginBottom: 12,
  },
  info: {
    color: '#d8c39a',
    marginBottom: 12,
    fontSize: 13,
  },
  successText: {
    color: '#9ce7b3',
    marginBottom: 12,
  },
  submitBtn: {
    width: '100%',
    padding: 12,
    borderRadius: 10,
    border: '1px solid #d7b16a',
    background: 'linear-gradient(135deg, #8b5a2b, #6f431f)',
    color: '#f6e7c1',
    fontWeight: 800,
    cursor: 'pointer',
    fontSize: 20,
    marginTop: 4,
  },
  googleBtn: {
    width: '100%',
    padding: 11,
    borderRadius: 10,
    border: '1px solid rgba(246,231,193,0.35)',
    background: 'rgba(20, 20, 20, 0.25)',
    color: '#f6e7c1',
    fontWeight: 800,
    cursor: 'pointer',
    fontSize: 16,
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  googleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: 999,
    background: '#fff',
    flex: '0 0 auto',
  },
  guestBtn: {
    width: '100%',
    padding: 11,
    borderRadius: 10,
    border: '1px solid rgba(212,165,88,0.75)',
    background: 'rgba(42,23,15,0.88)',
    color: '#f6e7c1',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 16,
    marginTop: 10,
  },
  secondaryBtn: {
    padding: '12px 14px',
    borderRadius: 10,
    border: '1px solid rgba(212,165,88,0.75)',
    background: 'rgba(42,23,15,0.88)',
    color: '#f6e7c1',
    fontWeight: 700,
    fontSize: 14,
    whiteSpace: 'nowrap',
  },
  otpRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 10,
    alignItems: 'stretch',
  },
  switchLine: {
    marginTop: 12,
    textAlign: 'center',
    color: '#d8c39a',
    fontSize: 14,
  },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    color: '#ffd48a',
    cursor: 'pointer',
    fontWeight: 700,
    textDecoration: 'underline',
    padding: 0,
    fontSize: 14,
  },
};
